import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ReportsClassificationColumn,
    ReportsClassificationFilters,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { ReportColumnId, REPORT_COLUMN_IDS } from "org/reports/list/columns";

import { decodeOrFilter, encodeOrFilter } from "../common";

import { decodeAccessRights, encodeAccessRights } from "./common";

import { ReportsPageStateRaw } from ".";

type ReportsClassificationPageStateWithType =
    PageStateData<"ReportsClassification"> & { type: "ReportsClassification" };

function encodeReportsClassificationPageState(
    raw: ReportsPageStateRaw
): ReportsClassificationPageStateWithType {
    if (raw.pageType !== "ReportsClassification") {
        throw new Error(
            `Expected page type to be "ReportsClassification", but got "${raw.pageType}"`
        );
    }

    return {
        type: "ReportsClassification",
        version: raw.version,
        columns: Array.from(raw.visibleColumnIds).map(
            encodeReportsClassificationColumn
        ),
        filters: encodeReportsClassificationFilters(raw.filter),
        leftSideMenuCollapsed: raw.leftSideMenuCollapsed,
    };
}

function decodeReportsClassificationPageState(
    input: ReportsClassificationPageStateWithType
): ReportsPageStateRaw {
    if (input.type !== "ReportsClassification") {
        throw new Error(
            `Expected page type to be "ReportsClassification", but got "${input.type}"`
        );
    }

    const visibleColumnIdsAsArray = input.columns.map(
        decodeReportsClassificationColumn
    );

    return {
        pageType: "ReportsClassification",
        version: input.version,
        visibleColumnIds: new Set(visibleColumnIdsAsArray),
        filter: decodeReportsClassificationFilters(input.filters),
        leftSideMenuCollapsed: input.leftSideMenuCollapsed,
    };
}

function encodeReportsClassificationColumn(
    rawColumn: ReportColumnId
): ReportsClassificationColumn {
    switch (rawColumn) {
        case REPORT_COLUMN_IDS.NAME:
            return "name";
        case REPORT_COLUMN_IDS.CLASSIFICATION:
            return "classification";
        case REPORT_COLUMN_IDS.DESCRIPTION:
            return "description";
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
            return "pushReportsCount";
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
            return "accessRights";
        case REPORT_COLUMN_IDS.ACTION:
            return "actions";
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
        case REPORT_COLUMN_IDS.DATASHEET:
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
        case REPORT_COLUMN_IDS.REPORT_TYPE:
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
            throw new Error(
                `Unknown column for ReportsClassification page: ${rawColumn}`
            );
        default:
            return assertNever(rawColumn);
    }
}

function decodeReportsClassificationColumn(
    column: ReportsClassificationColumn
): ReportColumnId {
    switch (column) {
        case "name":
            return REPORT_COLUMN_IDS.NAME;
        case "classification":
            return REPORT_COLUMN_IDS.CLASSIFICATION;
        case "description":
            return REPORT_COLUMN_IDS.DESCRIPTION;
        case "pushReportsCount":
            return REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT;
        case "accessRights":
            return REPORT_COLUMN_IDS.ACCESS_RIGHTS;
        case "actions":
            return REPORT_COLUMN_IDS.ACTION;
        default:
            return assertNever(column);
    }
}

function encodeReportsClassificationFilters(
    filter: SimpleAndFilter | null
): ReportsClassificationFilters {
    if (filter === null) {
        return {};
    }

    const filters = filter.filters;
    return {
        name: encodeOrFilter(REPORT_COLUMN_IDS.NAME, filters),
        description: encodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters),
        classification: encodeOrFilter(
            REPORT_COLUMN_IDS.CLASSIFICATION,
            filters
        ),
        pushReportsCount: encodeOrFilter(
            REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
            filters
        ),
        accessRights: encodeAccessRights(filters),
    };
}

function decodeReportsClassificationFilters(
    filters: ReportsClassificationFilters
): SimpleAndFilter | null {
    if (Object.keys(filters).length === 0) {
        return null;
    }

    const orFilters: Array<SimpleOrFilter> = [];
    if (filters.name) {
        orFilters.push(decodeOrFilter(REPORT_COLUMN_IDS.NAME, filters.name));
    }
    if (filters.classification) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.CLASSIFICATION,
                filters.classification
            )
        );
    }
    if (filters.description) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters.description)
        );
    }
    if (filters.pushReportsCount) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
                filters.pushReportsCount
            )
        );
    }
    if (filters.accessRights) {
        const accessRightFilters = decodeAccessRights(filters.accessRights);
        orFilters.push(...accessRightFilters);
    }

    return {
        filters: orFilters,
    };
}

export {
    encodeReportsClassificationPageState,
    decodeReportsClassificationPageState,
};

export type { ReportsClassificationPageStateWithType };
